import {
  Box,
  Typography,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import Loader from "Components/Loader";
import ImgPath from "Constants/Imgs";
import { CaseListType } from "DataTypes/Case.type";
import { UserInitialStateType } from "DataTypes/Redux";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CaseApi from "Service/Case.api";
import { disconnectChatHub } from "Service/signalRService";
import DateTime from "Utils/DateTime";
import { FormateCaseList } from "./Formatter";
import {
  filterByAnyValue,
  FormatDropdownData,
  HandleFormChange,
} from "Utils/Common";
import { useParams } from "react-router-dom";
import { GlobalResType, HandleChangeProps } from "DataTypes/Common";
import { DropdownArrPropsType, FormDataType } from "DataTypes/Form";
import ModalLayout from "Layouts/Modal";
import { CustomModal } from "Components/Modal";
import { AddNewCaseForm } from "./Data";

const ChatListComponent = (props: any) => {
  const { setShowChatList, getSelectedCase, onListLoaded } = props;
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const { orderType } = useParams<{ orderType: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [caseList, setCaseList] = useState<CaseListType[]>([]);
  const [orderDropdown, setOrderDropdown] = useState<DropdownArrPropsType[]>(
    []
  );
  const [caseDropdown, setCaseDropdown] = useState<DropdownArrPropsType[]>([]);
  const [caseListCopy, setCaseListCopy] = useState<CaseListType[]>([]);
  const [createCaseFormData, setCreateCaseFormData] = useState<FormDataType[]>(
    []
  );
  const [selectedCase, setSelectedCase] = useState<null | CaseListType>(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [inputVal, setInputVal] = useState<string>("");
  const [isLegalCounselChat, setIsLegalCounselChat] = useState<boolean>(false);

  useEffect(() => {
    handleOnload();
  }, []);

  useEffect(() => {
    if (inputVal) {
      const filteredList = filterByAnyValue(caseListCopy, inputVal);
      setCaseList(filteredList);
    } else {
      setCaseList(caseListCopy);
    }
  }, [inputVal]);

  useEffect(() => {
    return () => {
      // cleanup on chat screen exit
      // disconnectChatHub();
    };
  }, []);

  const handleOnload = async () => {
    setLoading(true);
    const caseRes: GlobalResType = await CaseApi.fetchCaseTypes();
    let caseTypeDropList: DropdownArrPropsType[] = [];
    let orderTypeDropList: DropdownArrPropsType[] = [];
    if (caseRes?.success) {
      caseTypeDropList = FormatDropdownData(caseRes?.data, "id", "name", "id");
      setCaseDropdown(caseTypeDropList);
    }

    const OrderTypeRes: GlobalResType = await CaseApi.fetchOrderTypes();

    if (OrderTypeRes?.success) {
      orderTypeDropList = FormatDropdownData(
        OrderTypeRes?.data,
        "id",
        "name",
        "id"
      );
      setOrderDropdown(orderTypeDropList);
    }

    // 1. Define your allowed legal IDs
    const targetIds = [43, 44, 51];
    // Check if the current order is in our list
    const isTargetOrder = targetIds.includes(Number(orderType));

    // Assign the object only if it's a target order
    const legalObject = isTargetOrder
      ? orderTypeDropList?.find(el => Number(el.id) === Number(orderType))
      : null;
    // console.log(legalObject, "legalObject");
    if (legalObject) {
      if (legalObject.value == orderType) {
        setIsLegalCounselChat(true);
      }
    }
    getChatListByUserId();
    setLoading(false);
  };

  const getChatListByUserId: () => void = async () => {
    const res = await CaseApi.getCaseListByUserId({
      clientId: myInfo.id,
      orderTypeId: orderType ?? null,
    });
    if (res?.success) {
      const formattedRes = FormateCaseList(res?.data);
      setCaseList(formattedRes);
      setCaseListCopy(formattedRes);
      // This sends the data back to the Parent
      if (onListLoaded) onListLoaded(formattedRes);
    }
  };

  const getInitials: (orderType: string) => string = (orderType) => {
    if (!orderType) return "";
    const words = orderType.split(" ");
    let initials = "";
    for (let i = 0; i < words.length && i < 2; i++) {
      initials += words[i].charAt(0).toUpperCase();
    }
    return initials;
  };

  const handleCreateCase = async () => {
    console.log("create case clicked", createCaseFormData);
    
    // Find the case type based on the order type and create the case hardcoded
    // 1. Define your allowed legal IDs
    const targetIds = [43, 44, 51];
    // 2. Find the specific object where the ID matches the current orderType 
    // AND ensure that ID is within your targetIds list
    const findCase = orderDropdown?.find((el) =>
      Number(el.id) === Number(orderType) && targetIds.includes(Number(el.id))
    );
    const payload = {
      clientId: myInfo.id,
      caseTypeId: findCase?.value ?? null,
      orderTypeId: orderType,
      description: createCaseFormData[0].value,
    };
    const res = await CaseApi.createCase(payload);
    if (res?.success) {
      setModalOpen(false);
      setCreateCaseFormData(JSON.parse(JSON.stringify(AddNewCaseForm)));
      getChatListByUserId();
    }
  };

  const HandleFormDataChange = (
    data: HandleChangeProps,
    arr: FormDataType[]
  ) => {
    setCreateCaseFormData(HandleFormChange(data, arr));
  };

  return (
    <>
      <ModalLayout
        content={
          <CustomModal.CreateCaseModal
            formArr={createCaseFormData}
            handleChange={HandleFormDataChange}
          />
        }
        open={modalOpen}
        modwidth="400px"
        onClose={() => setModalOpen(false)}
        heading="New Case"
        buttonArr={[
          {
            id: "createCase",
            label: "Create Case",
            clickHandler: handleCreateCase,
          },
          {
            id: "cancel",
            label: "Cancel",
            clickHandler: () => setModalOpen(false),
            type: "border",
          },
        ]}
      />
      <Box
        sx={{
          width: { xs: "100%", sm: 350 },
          height: "100%",
          borderRight: { sm: "1px solid #2a3162" },
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Chat List logic */}
        {/* Header and Search */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f7f7f7",
            borderBottom: "1px solid #2a3162",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f7f7f7",
              }}
            >
              Case List
            </Typography>
            {isLegalCounselChat && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setCreateCaseFormData(
                    JSON.parse(JSON.stringify(AddNewCaseForm))
                  );
                  setModalOpen(true);
                }}
                sx={{ textTransform: "none" }}
              >
                New Case
              </Button>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#fff",
              borderRadius: 2,
              px: 2,
              py: 0.5,
              boxShadow: 1,
            }}
          >
            <InputBase
              placeholder="Search or start new chat"
              fullWidth
              // value={""}
              sx={{ fontSize: "14px" }}
              inputProps={{ "aria-label": "search" }}
              onChange={(e) => setInputVal(e.target.value)} // Placeholder for search functionality
            />
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Loader />
          </Box>
        ) : (
          <List 
            disablePadding 
            sx={{ 
              flex: 1, 
              overflowY: "auto",
              minHeight: 0, // Important for flex child to be scrollable
            }}
          >
            {caseList.map((chat: CaseListType, index) => (
              <React.Fragment key={chat.caseId}>
                <ListItem
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    ...(selectedCase?.caseId == chat.caseId && {
                      ...sxSelected,
                    }),
                    alignItems: "flex-start",
                    py: 1.5,
                    px: 2,
                  }}
                  onClick={() => {
                    if (isMobile) setShowChatList(false);
                    setSelectedCase(chat);
                    getSelectedCase(chat);
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      alt={chat.caseType}
                      sx={{
                        backgroundColor: "secondary.main",
                        color: "text.primary",
                      }}
                    >
                      {getInitials(chat.orderType)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="body1"
                          // color="textPrimary"
                          fontWeight={600}
                        >
                          {isLegalCounselChat
                            ? `${chat?.description} - ${chat.caseId}`
                            : `${chat.orderType}  - ${chat.caseId} - ${chat.caseType}`}
                        </Typography>
                        <Typography variant="caption" color="textPrimary">
                          {DateTime.ToLocalDate(chat.statusLastUpdated)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index !== caseList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </>
  );
};
export default ChatListComponent;

const sxSelected = {
  backgroundColor: "#f3ecebff",
};
